import uniqueId from 'lodash/uniqueId';
import { SuiNotification } from '@splashthat/sui';
import { useQuery } from 'react-query';
import get from 'lodash/get';
import { camelizeKeys } from 'humps';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { useNotificationManager } from 'common/compounds/NotificationManager';
import { migrateEngine } from 'common/compounds/VirtualEventRenderer/utils/migrateEngine';
import { VIRTUAL_SETTINGS_ERROR_MESSAGE } from 'common/constants/virtualEvent';
import useGetRequestFlowStep, { REQUEST_PROCESS_STEP_STATES } from './useGetRequestFlowStep';
import useVirtualSettingsAttendeeApiClient from './useVirtualSettingsAttendeeApiClient';

const BAD_HASH = 'Bad hash';
const bootstrap = getBootstrap();
const eventId = parseInt(bootstrap.get('event.id', null), 10);

/**
 * Will retrieve event settings data from API automatically
 * when the requestProcessStep is set to "FETCH_EVENT_SETTINGS".
 *
 * @param {Object{ vepTokens }} props - An object with vepToken list
 * @return {Object} eventSettings - The current eventSettings react-query request object from the API.
 */
const useEventSettingsData = ({ vepTokens, enabled, onError }) => {
    const virtualEventSettingsAPI = useVirtualSettingsAttendeeApiClient({ vepTokens });
    const requestProcessStep = useGetRequestFlowStep({ vepTokens, enabled });
    const notificationManager = useNotificationManager();

    return useQuery(['eventSettings', eventId], () => virtualEventSettingsAPI.getVirtualEventSettings(eventId), {
        enabled: requestProcessStep === REQUEST_PROCESS_STEP_STATES.FETCH_EVENT_SETTINGS,
        refetchOnWindowFocus: false,
        select: ({ data }) => {
            const bootstrapEvent = getBootstrap().get('event', {});
            const bootstrapIsTermsAndConditionsNotificationEnabled = getBootstrap().get(
                'isTermsAndConditionsNotificationEnabled',
            );
            const isTermsAndConditionsNotificationEnabled = bootstrapIsTermsAndConditionsNotificationEnabled === 'true';
            const settings = camelizeKeys(get(data, 'settings', {}));
            const guest = camelizeKeys(get(data, 'guest', {}));
            const event = camelizeKeys(get(data, 'event', {}));
            const engine = migrateEngine(settings, get(settings, 'elementConfigs', {}));

            return {
                ...data,
                settings: {
                    ...settings,
                    engine: {
                        ...engine,
                        states: {
                            ...engine.states,
                        },
                    },
                },
                guest,
                event: {
                    ...bootstrapEvent,
                    ...event,
                    isTermsAndConditionsNotificationEnabled, // Used by the terms and conditions notification to determine if it should be shown.
                },
            };
        },
        onError: (e) => {
            if (e.data?.meta?.message === BAD_HASH) {
                onError();
            }
            notificationManager.showNotification({
                id: uniqueId('VEP_REQUEST_GET_VIRTUAL_SETTINGS_ERROR_'),
                message: VIRTUAL_SETTINGS_ERROR_MESSAGE,
                variation: SuiNotification.ERROR,
            });
        },
    });
};

export default useEventSettingsData;
